import React from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { QUERY_MEETINGS } from "../../../utils/graphql/meetings/queries.js";
import { QUERY_MATCH_ENGINE_DRY_RUN, QUERY_MATCH_ENGINE_JOB_DRY_RUN, QUERY_JOBS } from "../../../utils/graphql/jobs/queries.js";
import "./MatchEngineDryRun.css";

function DryRunResults({ result, loading, error }) {
  // Log errors to console for debugging
  React.useEffect(() => {
    if (error) {
      console.error('Match Engine Dry Run Error:', error);
      console.error('GraphQL Error Details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        extraInfo: error.extraInfo
      });
    }
  }, [error]);

  if (loading) return (
    <div className="loading-container">
      <div className="text-center">
        <div className="spinner-border" role="status" aria-hidden="true"></div>
        <div className="mt-2 text-muted">Running match engine analysis...</div>
      </div>
    </div>
  );
  
  if (error) {
    return (
      <div className="alert alert-danger">
        <div className="d-flex align-items-start">
          <i className="bi bi-exclamation-triangle-fill me-2 mt-1 flex-shrink-0"></i>
          <div className="flex-grow-1">
            <strong>Match Engine Error</strong>
            <div className="mt-2">
              <strong>Message:</strong> {error.message || 'Unknown error occurred'}
            </div>
            {error.graphQLErrors && error.graphQLErrors.length > 0 && (
              <div className="mt-2">
                <strong>Details:</strong>
                <ul className="mb-0 mt-1">
                  {error.graphQLErrors.map((err, index) => (
                    <li key={index}>{err.message}</li>
                  ))}
                </ul>
              </div>
            )}
            {error.networkError && (
              <div className="mt-2">
                <strong>Network Error:</strong> {error.networkError.message}
              </div>
            )}
            <div className="mt-3">
              <small className="text-muted">
                Check the browser console for more detailed error information.
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!result) return null;

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div className="dryrun-result-meta">
          <h5 className="mb-0">{result.meetingTitle || "Meeting"}</h5>
          <div className="dryrun-meta-line text-muted">
            Job ID: {result.jobId || "—"} • Constraints: {result.constraintCount}
            {result.workloadBalanceWindowDays && <> • Workload Window: {result.workloadBalanceWindowDays} days</>}
          </div>
          {result.scoringWeights && (
            <div className="dryrun-weight-chips mt-2">
              <span className="dryrun-weight-chip">Constraint {(result.scoringWeights.constraint * 100).toFixed(0)}%</span>
              <span className="dryrun-weight-chip">Hosted {(result.scoringWeights.meetingsHosted * 100).toFixed(0)}%</span>
              <span className="dryrun-weight-chip">Recent Subs {(result.scoringWeights.recentSubs * 100).toFixed(0)}%</span>
              <span className="dryrun-weight-chip">Total Subs {(result.scoringWeights.totalSubs * 100).toFixed(0)}%</span>
              <span className="dryrun-weight-chip">Unassigned Apps {(result.scoringWeights.unassignedApplications * 100).toFixed(0)}%</span>
              <span className="dryrun-weight-chip">Timing {(result.scoringWeights.applicationTiming * 100).toFixed(0)}%</span>
            </div>
          )}
          <div className="mt-2">
            {result.workloadBalanceWindowDays ? (
              <small className="text-muted">
                Workload balance is active. Recent assignments within {result.workloadBalanceWindowDays} days are included.
              </small>
            ) : (
              <>
                <small className="text-muted">
                  Workload balance is disabled for this meeting. {" "}
                  <button
                    className="btn btn-link p-0 text-decoration-underline"
                    style={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                    onClick={(e) => {
                      e.preventDefault();
                      alert('To enable recent-sub balancing:\n\n1. Go to Admin Settings → Meeting Settings\n2. Edit this meeting\n3. Set "Workload Balance Window (days)" field (e.g., 30)\n4. Save the meeting\n\nThen try the dry run again to see the "Recent Sub Load" column!');
                    }}
                  >
                    Learn how to enable it
                  </button>
                </small>
              </>
            )}
          </div>
        </div>
        {result.message && (
          <span className="badge text-bg-warning">{result.message}</span>
        )}
      </div>

      <div className="row">
        <div className="col-md-4">
          <h6>Constraint Fit Rules</h6>
          {result.constraints?.length ? (
            <ul className="list-group list-group-flush">
              {result.constraints.map((c) => (
                <li className="list-group-item px-0" key={c._id}>
                  <strong>{c.name}</strong>{" "}
                  {c.required && (
                    <span className="badge text-bg-danger ms-2">Required</span>
                  )}
                  <br />
                  <small className="text-muted">
                    ({c.fieldKey} {(c.operator || "").toLowerCase()} {c.value})
                  </small>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">No constraint fit rules found for this meeting.</div>
          )}
        </div>
        
        <div className="col-md-8">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6>Applicants ({result.applicants?.length || 0})</h6>
            {result.workloadBalanceWindowDays && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <small className="text-muted">Recent Sub Load:</small>
                <small className="badge dryrun-neutral-badge">
                  {result.applicants?.filter(app => (app.recentSubJobs ?? 0) === 0).length || 0} with 0 recent subs
                </small>
                <small className="badge dryrun-neutral-badge">
                  {result.applicants?.filter(app => (app.recentSubJobs ?? 0) >= 1).length || 0} with 1+ recent subs
                </small>
              </div>
            )}
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle match-engine-dryrun-table">
              <thead className="dryrun-table-head">
                <tr>
                  <th scope="col">Applicant</th>
                  <th scope="col" className="text-end">Constraint Fit</th>
                  <th scope="col" className="text-end">Meetings Hosted</th>
                  <th scope="col" className="text-end">Recent Subs</th>
                  <th scope="col" className="text-end">Total Subs</th>
                  <th scope="col" className="text-end">Unassigned Apps</th>
                  <th scope="col" className="text-end">Applied At</th>
                  <th scope="col" className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {result.applicants?.length ? (
                  result.applicants.map((app, index) => {
                    const w = result.scoringWeights;
                    const fmt = (v) => (typeof v === 'number' && Number.isFinite(v)) ? v.toFixed(3) : '—';
                    const contrib = (score, weight) => {
                      const s = Number(score); const wt = Number(weight);
                      return (Number.isFinite(s) && Number.isFinite(wt)) ? (s * wt).toFixed(3) : '—';
                    };
                    const weightedSum = w ? [
                      [app.constraintScore, w.constraint],
                      [app.workloadScore, w.meetingsHosted],
                      [app.recentSubScore, w.recentSubs],
                      [app.totalSubScore, w.totalSubs],
                      [app.unassignedApplicationScore, w.unassignedApplications],
                      [app.applicationTimingScore, w.applicationTiming],
                    ].reduce((sum, [s, wt]) => {
                      const sv = Number(s); const wv = Number(wt);
                      return (Number.isFinite(sv) && Number.isFinite(wv)) ? sum + sv * wv : sum;
                    }, 0).toFixed(3) : null;

                    return (
                      <tr key={app.applicationId || app.userId} className={index === 0 ? "dryrun-winner-row" : ""}>
                        <td>
                          <div className="fw-semibold">
                            {app.userName || "Unknown"}
                            {!app.isApplicant && <small className="text-muted ms-1">(synthetic)</small>}
                          </div>
                          <span className={`badge ${app.eligible ? 'text-bg-success' : 'text-bg-danger'}`} style={{ fontSize: '0.72rem' }}>
                            {app.eligible ? "Eligible" : "Disqualified"}
                          </span>
                        </td>
                        <td className="text-end"
                          title={app.matchedConstraints?.length ? `Matched: ${app.matchedConstraints.join(", ")}` : "No constraints matched"}
                          style={{ cursor: 'help' }}
                        >
                          <div className="fw-semibold">{`${app.matched ?? 0}/${app.total ?? 0}`}</div>
                          <small className="text-muted d-block">Score {fmt(app.constraintScore)}</small>
                          {w && <small className="text-muted d-block">Weighted {contrib(app.constraintScore, w.constraint)}</small>}
                        </td>
                        <td className="text-end">
                          <div className="fw-semibold">{app.meetingsHosted ?? 0}</div>
                          <small className="text-muted d-block">Score {fmt(app.workloadScore)}</small>
                          {w && <small className="text-muted d-block">Weighted {contrib(app.workloadScore, w.meetingsHosted)}</small>}
                        </td>
                        <td className="text-end">
                          <div className="fw-semibold">{app.recentSubJobs ?? 0}</div>
                          <small className="text-muted d-block">Score {fmt(app.recentSubScore)}</small>
                          {w && <small className="text-muted d-block">Weighted {contrib(app.recentSubScore, w.recentSubs)}</small>}
                        </td>
                        <td className="text-end">
                          <div className="fw-semibold">{app.totalSubJobs ?? 0}</div>
                          <small className="text-muted d-block">Score {fmt(app.totalSubScore)}</small>
                          {w && <small className="text-muted d-block">Weighted {contrib(app.totalSubScore, w.totalSubs)}</small>}
                        </td>
                        <td className="text-end">
                          <div className="fw-semibold">{app.unassignedApplicationJobs ?? 0}</div>
                          <small className="text-muted d-block">Score {fmt(app.unassignedApplicationScore)}</small>
                          {w && <small className="text-muted d-block">Weighted {contrib(app.unassignedApplicationScore, w.unassignedApplications)}</small>}
                        </td>
                        <td className="text-end">
                          <div className="fw-semibold">{app.appliedAt ? new Date(app.appliedAt).toLocaleString() : '—'}</div>
                          <small className="text-muted d-block">Score {fmt(app.applicationTimingScore)}</small>
                          {w && <small className="text-muted d-block">Weighted {contrib(app.applicationTimingScore, w.applicationTiming)}</small>}
                        </td>
                        <td className="text-end fw-semibold">
                          <div>{fmt(app.score)}</div>
                          {weightedSum !== null && <small className="text-muted d-block">Σ {weightedSum}</small>}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="text-muted text-center">
                      No applicants available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActiveJobsTab() {
  const { data: jobsData, loading: jobsLoading } = useQuery(QUERY_JOBS, { 
    variables: { showAll: false } // Only active jobs
  });
  const [
    runJobDryRun,
    { data: dryRunData, loading: dryRunLoading, error: dryRunError }
  ] = useLazyQuery(QUERY_MATCH_ENGINE_JOB_DRY_RUN, { fetchPolicy: "no-cache" });

  const [selectedJobId, setSelectedJobId] = React.useState("");

  const activeJobs = React.useMemo(() => {
    return (jobsData?.jobs ?? []).filter(job => job.active && !job.assignedTo);
  }, [jobsData]);

  const handleRun = React.useCallback(() => {
    if (!selectedJobId) return;
    console.log('Running job dry run for job ID:', selectedJobId);
    runJobDryRun({ variables: { jobId: selectedJobId } })
      .then(result => {
        console.log('Job dry run completed successfully:', result);
      })
      .catch(error => {
        console.error('Job dry run failed:', error);
      });
  }, [runJobDryRun, selectedJobId]);

  if (jobsLoading) return (
    <div className="loading-container">
      <div className="text-center">
        <div className="spinner-border" role="status" aria-hidden="true"></div>
        <div className="mt-2 text-muted">Loading active jobs...</div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="tab-description">
        <strong>Active Job Analysis:</strong> View match engine results for currently active substitute jobs that have applications.
        This shows how the system would rank actual applicants for real substitute opportunities.
      </div>

      <div className="enhanced-form-controls">
        <div className="row g-3 align-items-end mb-4">
          <div className="col-12 col-md-8">
            <label className="form-label fw-semibold">Select Active Job</label>
            <select
              className="form-select"
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              disabled={dryRunLoading}
            >
              <option value="">Choose an active substitute job…</option>
              {activeJobs.map((job) => (
                <option key={job._id} value={job._id}>
                  {job.meetingSnapshot?.title || "(Untitled)"} — 
                  {job.applicationCount || 0} applications — 
                  {job.meetingSnapshot?.startDateTime ? 
                    new Date(job.meetingSnapshot.startDateTime).toLocaleString() : 
                    "Date TBD"
                  }
                </option>
              ))}
            </select>
            {activeJobs.length === 0 && (
              <small className="text-muted mt-1 d-block">
                <i className="bi bi-info-circle me-1"></i>
                No active jobs with applications found.
              </small>
            )}
          </div>
          <div className="col-12 col-md-4">
            <button
              className="btn btn-primary w-100"
              onClick={handleRun}
              disabled={!selectedJobId || dryRunLoading}
            >
              {dryRunLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Analyzing...
                </>
              ) : (
                <>
                  <i className="bi bi-play-circle me-2"></i>
                  Run Job Analysis
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <DryRunResults 
        result={dryRunData?.matchEngineJobDryRun}
        loading={dryRunLoading}
        error={dryRunError}
      />
    </div>
  );
}

function AllMeetingsTab() {
  const { data: meetingsData, loading: meetingsLoading } = useQuery(QUERY_MEETINGS);
  const [
    runMeetingDryRun,
    { data: dryRunData, loading: dryRunLoading, error: dryRunError }
  ] = useLazyQuery(QUERY_MATCH_ENGINE_DRY_RUN, { fetchPolicy: "no-cache" });

  const [selectedMeetingId, setSelectedMeetingId] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredMeetings = React.useMemo(() => {
    const meetings = meetingsData?.meetings ?? [];
    if (!searchTerm) return meetings;
    
    const term = searchTerm.toLowerCase();
    return meetings.filter(meeting => 
      (meeting.summary || "").toLowerCase().includes(term) ||
      (meeting.description || "").toLowerCase().includes(term)
    );
  }, [meetingsData, searchTerm]);

  const handleRun = React.useCallback(() => {
    if (!selectedMeetingId) return;
    console.log('Running meeting dry run for meeting ID:', selectedMeetingId);
    runMeetingDryRun({ variables: { meetingId: selectedMeetingId } })
      .then(result => {
        console.log('Meeting dry run completed successfully:', result);
      })
      .catch(error => {
        console.error('Meeting dry run failed:', error);
      });
  }, [runMeetingDryRun, selectedMeetingId]);

  if (meetingsLoading) return (
    <div className="loading-container">
      <div className="text-center">
        <div className="spinner-border" role="status" aria-hidden="true"></div>
        <div className="mt-2 text-muted">Loading meetings...</div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="tab-description">
        <strong>Meeting Constraint Testing:</strong> Test constraint matching against any meeting (regardless of date) to see how users would rank.
        This helps you validate constraint configurations and understand user eligibility.
      </div>

      <div className="enhanced-form-controls">
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-4">
            <label className="form-label fw-semibold">Search Meetings</label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold">Select Meeting</label>
            <select
              className="form-select"
              value={selectedMeetingId}
              onChange={(e) => setSelectedMeetingId(e.target.value)}
              disabled={dryRunLoading}
            >
              <option value="">Choose a meeting to analyze…</option>
              {filteredMeetings.map((meeting) => (
                <option key={meeting._id} value={meeting._id}>
                  {meeting.summary || "(Untitled)"} — 
                  {meeting.constraintGroups?.length || 0} constraint groups — 
                  {meeting.updatedAt ? new Date(meeting.updatedAt).toLocaleDateString() : "Unknown date"}
                </option>
              ))}
            </select>
            {filteredMeetings.length === 0 && searchTerm && (
              <small className="text-muted mt-1 d-block">
                <i className="bi bi-search me-1"></i>
                No meetings match your search criteria.
              </small>
            )}
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">&nbsp;</label>
            <button
              className="btn btn-primary w-100 d-block"
              onClick={handleRun}
              disabled={!selectedMeetingId || dryRunLoading}
            >
              {dryRunLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Running...
                </>
              ) : (
                <>
                  <i className="bi bi-play-circle me-1"></i>
                  Test
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <DryRunResults 
        result={dryRunData?.matchEngineDryRun}
        loading={dryRunLoading}
        error={dryRunError}
      />
    </div>
  );
}

export function MatchEngineDryRun() {
  const [activeTab, setActiveTab] = React.useState("jobs");

  return (
    <div className="mb-4">
      <div className="tab-section-header">
        <div>
          <h2 className="mb-0">Match Engine Dry Run</h2>
          <small className="text-muted">
            Preview and test how applicants would rank without making any actual assignments.
          </small>
        </div>
      </div>

      {/* Enhanced Tab Navigation */}
      <ul className="nav nav-tabs match-engine-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "jobs" ? "active" : ""}`}
            onClick={() => setActiveTab("jobs")}
            data-tab="jobs"
          >
            Active Jobs
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "meetings" ? "active" : ""}`}
            onClick={() => setActiveTab("meetings")}
            data-tab="meetings"
          >
            All Meetings
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "jobs" && <ActiveJobsTab />}
        {activeTab === "meetings" && <AllMeetingsTab />}
      </div>
    </div>
  );
}